/**
 * Typed inventory problem narrowing.
 *
 * The contract publishes closed problem shapes with extension members the
 * screens must render (the fiscal period that refused a posting, the latest
 * accepted movement time, the stock browser link for a bin that still holds
 * stock). `Problem` in the http layer only types the RFC 9457 core, so each
 * guard below checks the extension members it reads at runtime and returns
 * the generated schema type — nothing hand-writes a wire shape.
 */
import type { components } from "@erp/api-client";

import { ApiError } from "@/lib/api/errors";
import type { Problem } from "@/lib/api/problem";

type Schemas = components["schemas"];

export type OccurredAtPeriodClosedProblem = Schemas["InventoryOccurredAtPeriodClosedProblem"];
export type OccurredAtBeforeLatestMovementProblem =
  Schemas["InventoryOccurredAtBeforeLatestMovementProblem"];
export type InsufficientStockProblem = Schemas["InventoryInsufficientStockProblem"];
export type UomUnconvertibleProblem = Schemas["InventoryUomUnconvertibleProblem"];
export type LocationHasStockProblem = Schemas["InventoryLocationHasStockProblem"];
export type WarehouseHasMovementsProblem = Schemas["InventoryWarehouseHasMovementsProblem"];
export type InvalidTransitionProblem = Schemas["InventoryInvalidTransitionProblem"];
export type CountIncompleteProblem = Schemas["InventoryCountIncompleteProblem"];
export type DuplicateCodeProblem = Schemas["InventoryDuplicateCodeProblem"];
export type DefaultLocationInUseProblem = Schemas["InventoryDefaultLocationInUseProblem"];
export type ImmutableDocumentProblem = Schemas["InventoryImmutableDocumentProblem"];

/** Stable machine codes this feature reacts to by name. */
export const INVENTORY_PROBLEM_CODES = {
  occurredAtPeriodClosed: "occurred_at_period_closed",
  occurredAtBeforeLatestMovement: "occurred_at_before_latest_movement",
  insufficientStock: "insufficient_stock",
  uomUnconvertible: "uom_unconvertible",
  locationHasStock: "location_has_stock",
  warehouseHasMovements: "warehouse_has_movements",
  invalidTransition: "invalid_transition",
  countIncomplete: "count_incomplete",
  duplicateCode: "duplicate_code",
  defaultLocationInUse: "default_location_in_use",
  immutableDocument: "immutable_inventory_document",
} as const;

/** The problem body carried by a thrown error, when there is one. */
export function problemOf(error: unknown): Problem | undefined {
  return error instanceof ApiError ? error.problem : undefined;
}

function bag(error: unknown): Record<string, unknown> | undefined {
  const problem = problemOf(error);
  return problem === undefined ? undefined : (problem as unknown as Record<string, unknown>);
}

function hasCode(error: unknown, code: string): boolean {
  return problemOf(error)?.code === code;
}

function str(source: Record<string, unknown>, key: string): string | undefined {
  const value = source[key];
  return typeof value === "string" ? value : undefined;
}

/** 422 — the movement date falls inside a soft-closed or locked fiscal period. */
export function asPeriodClosedProblem(error: unknown): OccurredAtPeriodClosedProblem | null {
  if (!hasCode(error, INVENTORY_PROBLEM_CODES.occurredAtPeriodClosed)) return null;
  const source = bag(error);
  if (source === undefined) return null;
  const period = str(source, "period");
  const periodState = str(source, "period_state");
  if (period === undefined || (periodState !== "soft_closed" && periodState !== "locked")) {
    return null;
  }
  return source as unknown as OccurredAtPeriodClosedProblem;
}

/** 422 — backdating before the latest accepted movement for this stock key. */
export function asBeforeLatestMovementProblem(
  error: unknown,
): OccurredAtBeforeLatestMovementProblem | null {
  if (!hasCode(error, INVENTORY_PROBLEM_CODES.occurredAtBeforeLatestMovement)) return null;
  const source = bag(error);
  if (source === undefined) return null;
  if (str(source, "latest_occurred_at") === undefined) return null;
  return source as unknown as OccurredAtBeforeLatestMovementProblem;
}

/** 409 — the issue/adjustment/transfer would drive a position negative. */
export function asInsufficientStockProblem(error: unknown): InsufficientStockProblem | null {
  if (!hasCode(error, INVENTORY_PROBLEM_CODES.insufficientStock)) return null;
  const source = bag(error);
  return source === undefined ? null : (source as unknown as InsufficientStockProblem);
}

/** 422 — entry UoM has no conversion to the variant's base UoM. */
export function asUomUnconvertibleProblem(error: unknown): UomUnconvertibleProblem | null {
  if (!hasCode(error, INVENTORY_PROBLEM_CODES.uomUnconvertible)) return null;
  const source = bag(error);
  if (source === undefined) return null;
  if (str(source, "from_uom") === undefined || str(source, "to_uom") === undefined) return null;
  return source as unknown as UomUnconvertibleProblem;
}

/** 409 — the bin still holds stock; it carries the filtered browser link. */
export function asLocationHasStockProblem(error: unknown): LocationHasStockProblem | null {
  if (!hasCode(error, INVENTORY_PROBLEM_CODES.locationHasStock)) return null;
  const source = bag(error);
  if (source === undefined) return null;
  if (str(source, "location_id") === undefined) return null;
  return source as unknown as LocationHasStockProblem;
}

/** 409 — archive refused: the warehouse already carries ledger movements. */
export function asWarehouseHasMovementsProblem(
  error: unknown,
): WarehouseHasMovementsProblem | null {
  if (!hasCode(error, INVENTORY_PROBLEM_CODES.warehouseHasMovements)) return null;
  const source = bag(error);
  if (source === undefined) return null;
  if (str(source, "warehouse_id") === undefined) return null;
  return source as unknown as WarehouseHasMovementsProblem;
}

/** 409 — the document is no longer in a state that accepts this action. */
export function isInvalidTransition(error: unknown): boolean {
  return hasCode(error, INVENTORY_PROBLEM_CODES.invalidTransition);
}

/** 409 — submission/approval refused because lines are still uncounted. */
export function isCountIncomplete(error: unknown): boolean {
  return hasCode(error, INVENTORY_PROBLEM_CODES.countIncomplete);
}

/** 409 — the warehouse or bin code is already taken. */
export function isDuplicateCode(error: unknown): boolean {
  return hasCode(error, INVENTORY_PROBLEM_CODES.duplicateCode);
}

/** 409 — the bin is still a warehouse default and cannot be retired. */
export function isDefaultLocationInUse(error: unknown): boolean {
  return hasCode(error, INVENTORY_PROBLEM_CODES.defaultLocationInUse);
}

/** 409 — a posted, immutable document refused the write. */
export function isImmutableDocument(error: unknown): boolean {
  return hasCode(error, INVENTORY_PROBLEM_CODES.immutableDocument);
}
